import { apiClient } from '../axiosInstance';
import { payClient } from '../payClient';
import { ENDPOINTS } from '../endpoints';
import {
  rememberDocumentLinkExchangePayload,
  rememberQuotePayload,
  rememberQuoteRows,
} from '../publicIds';
import type {
  FinancingOptionsParams,
  FinancingOptionsResponse,
  DocumentLinkExchangeWire,
  DocumentLinkSession,
  QuoteReportParams,
  QuoteReportResponse,
  QuoteRow,
  QuoteService,
} from './interfaces/QuoteService';
import {
  adaptQuoteReportResponse,
  type QuoteReportResponseWire,
} from './adapters/quoteRow';
import { adaptQuoteDetail, type QuoteDetailWire } from './adapters/quoteDetail';
import type { QuoteDetail } from '../../stores/useQuotesStore';
import { useAuthStore } from '../../stores/useAuthStore';

export const quoteService: QuoteService = {
  async fetchQuoteReport(
    params: QuoteReportParams,
  ): Promise<QuoteReportResponse> {
    // Wire shape is the flat Consumer::QuotesDatatable row (dollar total
    // string, snake_case, nullable name + HTML summary). The adapter reshapes
    // total to integer cents and passes name/summary through verbatim. See
    // adapters/quoteRow.ts for the contract.
    const { data } = await apiClient.get<QuoteReportResponseWire>(
      ENDPOINTS.reports.consumerQuotes,
      {
        params: {
          page: params.page + 1,
          per_page: params.rows,
          sort_by: params.sortField,
          sort_desc:
            params.sortOrder === undefined
              ? undefined
              : params.sortOrder === -1,
          query: params.filters?.global?.value,
          quote_status: params.quoteStatus,
        },
      },
    );
    rememberQuoteRows(data);
    return adaptQuoteReportResponse(data);
  },

  // D-04: URL-driven proceed. The URL is caller-supplied (the row's
  // server-minted proceed_url for 5.4, or quote.links.quote_proceed.href
  // for 5.5 detail), so there is no endpoint constant — POST { notes } to
  // it verbatim. Notes is optional; defaults to '' to keep the wire shape
  // stable across callers that don't collect notes.
  async proceedQuote(proceedUrl: string, notes?: string): Promise<void> {
    await apiClient.post(proceedUrl, { notes: notes ?? '' });
  },

  // Phase 5.5 D-13 — fetch the rich quote-detail wire. The endpoint is
  // customer-scoped: /v2/customers/:customer_id/quotes/:id. customerId is
  // read from the auth store (the consumer route /quotes/:id only carries
  // the quote id; the customer id is bound to the authenticated session).
  // The wire response is run through adaptQuoteDetail — see
  // adapters/quoteDetail.ts for the field-by-field normalization contract
  // (lowercased status, *_cents defensive coerce, optional-field omits,
  // HATEOAS links passthrough).
  //
  // 260611-gfb — always request grouped line items via `item_grouping=1`
  // (legacy parity, mirrors `consumer-portal/src/store.ts:82-91` and the
  // InvoiceService.getInvoice D-G2 wiring). No FeatureFlag/tenant gating —
  // harmless for flat quotes (ungrouped items just gain `type:'lineItem'`
  // and the adapter renders them identically). The Rails wire signal is the
  // `before_action :check_item_grouping` in base_api_controller.rb that
  // sets `@item_grouping` from `params[:item_grouping].to_boolean`; the
  // `_quote.json.jbuilder` partial then switches to `_grouped_items.json`.
  async getQuote(id: number): Promise<QuoteDetail> {
    const customerId = useAuthStore().user?.id ?? 0;
    const { data } = await apiClient.get<QuoteDetailWire>(
      ENDPOINTS.v2.customers.quotes(customerId, id),
      { params: { item_grouping: 1 } },
    );
    rememberQuotePayload(data);
    return adaptQuoteDetail(data);
  },

  // TC-708 — the 200 body is the same payload as getQuote (`render :show`), so it is
  // adapted and returned; 422s carry `{ message }` and are rethrown to the caller.
  async signQuote(
    id: number,
    signatureImage: string,
    notes = '',
  ): Promise<QuoteDetail> {
    const customerId = useAuthStore().user?.id ?? 0;
    const { data } = await apiClient.post<QuoteDetailWire>(
      ENDPOINTS.v2.customers.quoteSign(customerId, id),
      { signature_image: signatureImage, notes },
    );
    rememberQuotePayload(data);
    return adaptQuoteDetail(data);
  },

  // Phase 5.5 D-13 — fetch financing offers (Service Finance integration).
  // Async-loading wire: first poll returns { loading: true, data: null };
  // subsequent polls flip loading to false. The polling loop + 30s cap
  // (D-02) and the debounced-input requestId stale-guard (D-03) live in
  // the store action — the service forwards the raw wire and lets the
  // store own the loop semantics. axios drops `down_payment_cents` from
  // the query when undefined, so the very first fetch ships without it.
  async fetchFinancingOptions({
    customerId,
    quoteId,
    downPaymentCents,
  }: FinancingOptionsParams): Promise<FinancingOptionsResponse> {
    const { data } = await apiClient.get<FinancingOptionsResponse>(
      ENDPOINTS.v2.customers.quoteFinancingOptions(customerId, quoteId),
      { params: { down_payment_cents: downPaymentCents } },
    );
    return data;
  },

  // No-login job quotes link. These mirror the pay-link calls in
  // InvoiceService: the scoped payClient (never apiClient, whose 401 handler
  // logs the user out) and a per-call temp_token, never a module global.
  async exchangeDocumentLink(
    linkToken: string,
  ): Promise<DocumentLinkExchangeWire> {
    const { data } = await payClient.post<DocumentLinkExchangeWire>(
      ENDPOINTS.v2.links.exchange(linkToken),
    );
    rememberDocumentLinkExchangePayload(data);
    return data;
  },

  async fetchQuotesViaLink(session: DocumentLinkSession): Promise<QuoteRow[]> {
    const { data } = await payClient.get<QuoteReportResponseWire>(
      ENDPOINTS.reports.consumerQuotes,
      {
        params: {
          page: 1,
          per_page: 200,
          sort_by: 'id',
          sort_desc: true,
          temp_token: session.tempToken,
        },
      },
    );
    rememberQuoteRows(data);
    return adaptQuoteReportResponse(data).data;
  },

  async getQuoteViaLink(
    session: DocumentLinkSession,
    id: number,
  ): Promise<QuoteDetail> {
    const { data } = await payClient.get<QuoteDetailWire>(
      ENDPOINTS.v2.customers.quotes(session.customerId, id),
      { params: { item_grouping: 1, temp_token: session.tempToken } },
    );
    rememberQuotePayload(data);
    return adaptQuoteDetail(data);
  },

  // proceedUrl is the row's server-minted absolute URL, POSTed verbatim.
  async proceedQuoteViaLink(
    session: DocumentLinkSession,
    proceedUrl: string,
    notes?: string,
  ): Promise<void> {
    await payClient.post(
      proceedUrl,
      { notes: notes ?? '' },
      { params: { temp_token: session.tempToken } },
    );
  },

  async signQuoteViaLink(
    session: DocumentLinkSession,
    id: number,
    signatureImage: string,
    notes = '',
  ): Promise<QuoteDetail> {
    const { data } = await payClient.post<QuoteDetailWire>(
      ENDPOINTS.v2.customers.quoteSign(session.customerId, id),
      { signature_image: signatureImage, notes },
      { params: { temp_token: session.tempToken } },
    );
    rememberQuotePayload(data);
    return adaptQuoteDetail(data);
  },
};
